// src/components/WhyChooseUsSection.jsx
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const WhyChooseUsSection = () => {
  // img_dropped_image.png के लिए ref
  const droppedImageRef = useRef(null);

  const sectionRef = useRef(null);

  useEffect(() => {
    const context = gsap.context(() => {
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content")
      );

      // --- 1. Scrubbed Text Reveal Animation (Merise.ae style) ---
      textElements.forEach((element, index) => {
        // Set Initial State: Hidden (opacity: 0), slightly down (y: 25), and clipped from the bottom
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element.closest("div"),
            start: "top 85%", // Animation shuru karo jab element screen mein aana shuru ho
            end: "center 60%", // Animation end karo jab element screen mein lagbhag center ho
            scrub: 1, // Scroll se smooth link (scrubbing)
            // markers: true,
          },
        });

        // Define the Target State: Visible (opacity: 1), original position (y: 0), and fully unclipped
        // Scroll down karne par (timeline forward), text 25px se 0px par aayega (Neeche se Upar).
        tl.to(element, {
          y: 0, // Final state: Original position
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out", // Thoda smooth movement
          delay: index * 0.1, // Stagger effect for line-by-line reveal
        });
      });

      // --- 2. Scrubbed Divider Line Animation (Grow from Left) ---
      const lineElement = sectionRef.current.querySelector(
        ".about-divider-line"
      );
      if (lineElement) {
        // Initial State: Hidden (scaleX: 0) and set origin to the left
        gsap.set(lineElement, { scaleX: 0, transformOrigin: "left" });

        gsap.to(lineElement, {
          scaleX: 1, // Target state is visible (full width)
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: lineElement.closest(".about-subtitle"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }
    }, sectionRef);
    gsap.to(droppedImageRef.current, {
      rotation: 360, // 360 degree rotation
      duration: 3,
      ease: "none",
      scrollTrigger: {
        trigger: ".right-content-container",
        start: "top bottom", // जब कंटेनर नीचे से दिखाई दे
        end: "bottom top", // जब कंटेनर ऊपर से गायब हो जाए
        scrub: 1, // Scroll से लिंक, smooth rotation
      },
    });
    return () => context.revert();
  }, []);
  return (
    <section
      ref={sectionRef}
      className="why-choose-us-section w-full bg-[#1c233a] mt-[50px] md:mt-[20px] 2xl:mt-[100px]"
    >
      <div className="relative w-full h-auto py-[32px] sm:py-[50px] 2xl:py-[75px]">
        {/* Main Content Container */}
        <div className="flex flex-col lg:flex-row justify-between items-start w-full h-auto px-[32px] 2xl:px-0 pl-[32px] lg:pl-[80px] 2xl:pl-[150px] relative">
          {/* Left Content */}
          <div className="flex flex-col gap-[29px] sm:gap-[43px] 2xl:gap-[58px] justify-start items-start w-full 2xl:w-[72%] mb-8 lg:mb-0">
            {/* Header Section */}
            <div className="flex flex-col gap-[11px] sm:gap-[16px] 2xl:gap-[22px] justify-start items-start w-full">
              {/* 1. Subtitle & Divider */}
              <div className="flex flex-row justify-start items-center w-full overflow-hidden">
                <p className="animated-text-content text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[20px] sm:leading-[25px] 2xl:leading-[31px] text-left text-white font-[Sora]">
                  Why Choose Us?
                </p>
                <div className="header-divider w-[100px] sm:w-[150px] 2xl:w-[200px] h-[1px] sm:h-[1.5px] 2xl:h-[2px] bg-white ml-[15px] sm:ml-[22px] 2xl:ml-[30px]"></div>
              </div>
              {/* 2. Main Heading */}
              <div className="overflow-hidden">
                <h2 className="animated-text-content text-[30px] sm:text-[45px] 2xl::text-[60px] font-bold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-white font-[Sora]">
                  Why Our Solutions Make a Difference
                </h2>
              </div>
              {/* 3. Description Paragraph */}
              <div className="overflow-hidden">
                <p className="animated-text-content text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] w-full">
                  Providing innovative organic fertilizers and bio gas solutions
                  to promote sustainability, enhance soil health, reduce carbon
                  footprint, support renewable energy, and create a greener
                  future for agriculture and environmental well-being worldwide.
                </p>
              </div>
            </div>

            {/* Features List */}
            <div className="flex flex-col gap-[11px] sm:gap-[16px] 2xl:gap-[22px] justify-start items-end 2xl:w-[65%]">
              {/* Feature 1 */}
              <div className="feature-item flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-end w-full">
                <div className="flex flex-row gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-end items-center w-full">
                  <img
                    src="/images/plantIcon.png"
                    alt="Eco Icon"
                    className="feature-icon w-[19px] sm:w-[28px] 2xl:w-[38px] h-[19px] sm:h-[28px] 2xl:h-[38px]"
                  />
                  <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-center items-start self-center w-full">
                    {/* Feature 1 Heading */}
                    <div className="overflow-hidden my-[30px] md:my-[50px]">
                      <h3 className="text-[18px] sm:text-[24px] lg:text-[30px] font-bold leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        Large-Scale Biogas & Bio-CNG Production
                      </h3>
                    </div>
                    {/* Feature 1 Text */}
                    {/* <div className="overflow-hidden">
                      <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        We guarantee sustainable growth using bio-solutions that
                        nurture the environment and reduce reliance on fossil
                        fuels.
                      </p>
                    </div> */}
                    {/* Divider Line */}
                    <div className="feature-divider w-[100%] h-[0.5px] sm:h-[0.75px] 2xl:h-[1px] bg-white"></div>
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="feature-item flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-end w-full">
                <div className="flex flex-row gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-end items-center w-full">
                  <img
                    src="/images/plantIcon.png"
                    alt="Cost-Effective Icon"
                    className="feature-icon w-[19px] sm:w-[28px] 2xl:w-[38px] h-[19px] sm:h-[28px] 2xl:h-[38px]"
                  />
                  <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-start items-start self-center w-full">
                    {/* Feature 2 Heading */}
                    <div className="overflow-hidden my-[30px] md:my-[50px]">
                      <h3 className="text-[18px] sm:text-[24px] 2xl:text-[30px] font-bold leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        High-Impact Organic Waste Management
                      </h3>
                    </div>
                    {/* Feature 2 Text */}
                    {/* <div className="overflow-hidden">
                      <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        We provide stable, efficient Bio-CNG, helping industries
                        transition to green energy without compromising
                        profitability.
                      </p>
                    </div> */}
                    <div className="feature-divider w-[100%] h-[0.5px] sm:h-[0.75px] 2xl:h-[1px] bg-white"></div>
                  </div>
                </div>
                {/* Divider Line */}
              </div>

              {/* Feature 3 */}
              <div className="feature-item flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-end w-full">
                <div className="flex flex-row gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-end items-center w-full">
                  <img
                    src="/images/plantIcon.png"
                    alt="Quality Icon"
                    className="feature-icon w-[19px] sm:w-[28px] 2xl:w-[38px] h-[19px] sm:h-[28px] 2xl:h-[38px]"
                  />
                  <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-start items-start self-center w-full">
                    {/* Feature 3 Heading */}
                    <div className="overflow-hidden my-[30px] md:my-[50px]">
                      <h3 className="text-[18px] sm:text-[24px] 2xl:text-[30px] font-bold leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        Dual Output: Clean Energy & Organic Fertilizers
                      </h3>
                    </div>
                    {/* Feature 3 Text */}
                    {/* <div className="overflow-hidden">
                      <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        Our fertilizers and Bio-CNG meet stringent standards,
                        ensuring certified safety and reliable, high-quality
                        performance.
                      </p>
                    </div> */}
                    {/* Divider Line */}
                    <div className="feature-divider w-[100%] h-[0.5px] sm:h-[0.75px] 2xl:h-[1px] bg-white"></div>
                  </div>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="feature-item flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-end w-full">
                <div className="flex flex-row gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-end items-center w-full">
                  <img
                    src="/images/plantIcon.png"
                    alt="Support Icon"
                    className="feature-icon w-[19px] sm:w-[28px] 2xl:w-[38px] h-[19px] sm:h-[28px] 2xl:h-[38px]"
                  />
                  <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-start items-start self-center w-full">
                    {/* Feature 4 Heading */}
                    <div className="overflow-hidden my-[30px] md:my-[50px]">
                      <h3 className="text-[18px] sm:text-[24px] 2xl:text-[30px] font-bold leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        Strong Focus on Soil Health & Farmer Prosperit{" "}
                      </h3>
                    </div>
                    {/* Feature 4 Text */}
                    {/* <div className="overflow-hidden">
                      <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora]  animated-text-content">
                        We empower both agricultural and industrial partners
                        with the resources needed for a successful, sustainable,
                        and eco-friendly operational model.
                      </p>
                    </div> */}
                    {/* Divider Line */}
                    <div className="feature-divider w-[100%] h-[0.5px] sm:h-[0.75px] 2xl:h-[1px] bg-white"></div>
                  </div>
                </div>
              </div>

              <div className="feature-item flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-end w-full">
                <div className="flex flex-row gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-end items-center w-full">
                  <img
                    src="/images/plantIcon.png"
                    alt="Support Icon"
                    className="feature-icon w-[19px] sm:w-[28px] 2xl:w-[38px] h-[19px] sm:h-[28px] 2xl:h-[38px]"
                  />
                  <div className="flex flex-col gap-[5px] sm:gap-[7px] 2xl:gap-[10px] justify-start items-start self-center w-full">
                    {/* Feature 5 Heading */}
                    <div className="overflow-hidden my-[30px] md:my-[50px]">
                      <h3 className="text-[18px] sm:text-[24px] 2xl:text-[30px] font-bold leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora] animated-text-content">
                        Circular Economy, Zero-Waste Operations
                      </h3>
                    </div>
                    {/* Feature 4 Text */}
                    {/* <div className="overflow-hidden">
                      <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-white font-[Sora]  animated-text-content">
                        We empower both agricultural and industrial partners
                        with the resources needed for a successful, sustainable,
                        and eco-friendly operational model.
                      </p>
                    </div> */}
                    {/* Divider Line */}
                    <div className="feature-divider w-[100%] h-[0.5px] sm:h-[0.75px] 2xl:h-[1px] bg-white"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="right-content-container hidden xl:block absolute right-0 overflow-hidden w-[42%]">
            <div>
              <img
                ref={droppedImageRef} // Ref added here
                src="/images/img_dropped_image.webp"
                alt="Sustainable Solutions"
                // rounded-full class added to make the image round
                className="main-image-container max-w-[fit-content] h-full object-contain rounded-full relative right-[-100px]"
              />
            </div>
            <div className="absolute top-0 right-[0px] w-full h-full flex justify-end items-center pointer-events-none">
              <img
                src="/images/img_demeca_biokaasulaitos_malli.png"
                alt="Bio Gas Plant Model"
                className="model-image w-full h-full object-contain "
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
